import { magic } from '@gig/magic-sdk';
import {
    signUpCoreX,
    buildSSOSignUpCoreXModel,
    ISignupCoreXModel,
} from '@gig/magic-corex';
import {
    USER_LOGGED_IN_UPDATED,
    USER_SESSION_REFRESH,
} from '@gig/magic-messages';
import { toolkit } from '@gig/magic-frontend-toolkit';
import { getTrackingService, languageQuery } from '@gig/magic-common';
import type { SocialProvider } from '@gig/magic-common/dist/services/tracking/tracking-v2.interface';
import { IPage } from '@gig/magic-sdk/dist/adapter/pages/get';
import type { ISubscription } from '@gig/magic-message-bus/dist/base-bus';

const SESSION_REFRESH_ATTEMPTS = 5;
const SESSION_REFRESH_TIMEOUT_MS = 4000;

async function handleIncompleteSSO() {
    const incompleteSSO = magic.session.hasIncompleteSSOSignUp();
    if (!incompleteSSO || typeof incompleteSSO === 'boolean') {
        return;
    }

    const socialProvider = (incompleteSSO.identityProvider ??
        null) as SocialProvider;
    const utmParams = toolkit.affiliate.getUtmParams();

    const model = await enrichSignUpWithDocs(
        enrichSignUpWithUtm(buildSSOSignUpCoreXModel(incompleteSSO)),
    );
    await signUpCoreX(model);
    await refreshSessionUntilLoggedIn();

    // Auto-SSO path: signUpCoreX() is a direct API call with no UI component,
    // so registration tracking must be fired here explicitly.
    // Both events fire together — SSO bypasses email/SMS verification (INIT-971).
    getTrackingService().trackRegistrationComplete(
        model.email || '',
        utmParams,
        undefined,
        socialProvider,
    );
    getTrackingService().trackRegistrationConfirmed(
        utmParams,
        undefined,
        socialProvider,
    );

    window.commandBus.publish(USER_SESSION_REFRESH);
}

async function refreshSessionUntilLoggedIn(): Promise<void> {
    for (let attempt = 0; attempt < SESSION_REFRESH_ATTEMPTS; attempt++) {
        if (magic.session.getUser()) {
            return;
        }

        window.commandBus.publish(USER_SESSION_REFRESH);

        if (await waitForLogin(SESSION_REFRESH_TIMEOUT_MS)) {
            return;
        }
    }
}

function waitForLogin(timeoutMs: number): Promise<boolean> {
    return new Promise((resolve) => {
        let subscription: ISubscription | null = null;
        let settled = false;

        const finish = (loggedIn: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            subscription?.unsubscribe();
            resolve(loggedIn);
        };

        const timer = setTimeout(() => finish(false), timeoutMs);

        subscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            (e) => {
                if (e.payload && magic.session.getUser()) {
                    finish(true);
                }
            },
            { replay: true },
        );

        if (settled) {
            subscription.unsubscribe();
        }
    });
}

async function enrichSignUpWithDocs(
    model: ISignupCoreXModel,
): Promise<ISignupCoreXModel> {
    try {
        const lang = await languageQuery();

        const slugPrivacy = 'keycloak-privacy-cookie-policy';
        const slugTerms = 'keycloak-tnc';

        const [privacyPageRes, termsPageRes] = await Promise.allSettled([
            magic.methods.pages.get({ slug: slugPrivacy, lang }),
            magic.methods.pages.get({ slug: slugTerms, lang }),
        ]);

        if (privacyPageRes.status === 'fulfilled') {
            model.privacyPolicyVersion =
                ((privacyPageRes.value as IPage).version as string) ||
                model.privacyPolicyVersion;
        }

        if (termsPageRes.status === 'fulfilled') {
            model.termsAndConditionsVersion =
                ((termsPageRes.value as IPage).version as string) ||
                model.termsAndConditionsVersion;
        }
    } catch (error) {
        console.warn('[sso-signup] Failed to enrich sign up with docs', error);
    }

    return model;
}

function enrichSignUpWithUtm(model: ISignupCoreXModel): ISignupCoreXModel {
    const utmTags = toolkit.affiliate.getUtmParams();
    return {
        ...model,
        ...(utmTags as Partial<ISignupCoreXModel>),
    };
}

export function initIncompleteSSORegistration(): Promise<void> {
    return handleIncompleteSSO().catch((error: unknown) => {
        console.warn('[sso-signup] Failed to auto-register SSO flow', error);
    });
}
